import queryString from 'query-string'

export const topicUrlViaPathway = `/pathway/:pathwayId/topic/:topicId`
export const topicUrlViaCertification = `/certifications/:certificationId/topic/:topicId`
export const topicUrl = `/topic/:topicId/:topicVersion?`
export const eventUrl = `/event/:eventId`
export const eventSeriesUrl = `/event/series/:eventId`
export const workspaceUrl = `/workspace/:workspaceId`
export const surveyUrl = `/surveys/:surveyId`
export const pathwayUrl = `/pathway/:pathwayId/:pathwayVersion?`
export const certificationUrl = `/certifications/:certificationId`
export const certificationManage = `/certifications/:certificationId/manage`

/**
 * a central URL path link builder for all app routes
 */
export const linkBuilder = {
  topicUrl(topicId: number, showVersionsDrawer = false): string {
    return `/topic/${topicId}${showVersionsDrawer ? '?showVersionsDrawer' : ''}`
  },
  topicUrlWithVersion(
    topicId: number,
    topicVersion: string,
    showVersionsDrawer = false
  ): string {
    return `/topic/${topicId}/${topicVersion}${
      showVersionsDrawer ? '?showVersionsDrawer' : ''
    }`
  },
  topicPreviewUrl(topicId: number): string {
    return `/topic/${topicId}/preview`
  },
  topicUrlViaPathway(pathwayId: number, topicId: number): string {
    return `/pathway/${pathwayId}/topic/${topicId}`
  },
  topicUrlViaCertification(certificationId: number, topicId: number): string {
    return `/certifications/${certificationId}/topic/${topicId}`
  },
  workspaceUrl(workspaceId: number, themeId?: number): string {
    return `/workspace/${workspaceId}${themeId ? `#${themeId}` : ''}`
  },
  pathwayDetailManage(pathwayId: number): string {
    return `/managePathway/${pathwayId}`
  },
  pathwayDetailManageMembers(pathwayId: number): string {
    return `/managePathway/${pathwayId}/access`
  },
  surveyUrl(surveyId: number): string {
    return `/surveys/${surveyId}`
  },
  surveyPreview(surveyId: number): string {
    return `/surveys/${surveyId}/manage/preview`
  },
  surveyDetailManage(surveyId: number, tab?: string): string {
    return `/surveys/${surveyId}/manage${tab ? `/${tab}` : ''}`
  },
  workspaceManage(workspaceId: number): string {
    return `/workspace/${workspaceId}/manage`
  },
  workspaceMembers(workspaceId: number): string {
    return `/workspace/${workspaceId}/manage/access`
  },
  workspaceBookmarks(workspaceId: number): string {
    return `/workspace/${workspaceId}/manage/bookmarks`
  },
  workspaceDescription(workspaceId: number): string {
    return `/workspace/${workspaceId}/manage/description`
  },
  topicEditUrl(topicId: number): string {
    return `/topic/${topicId}/edit`
  },
  topicFeedbackUrl(topicId: number, feedbackTopicEnrolmentId: number): string {
    return `/topic/${topicId}/feedback/${feedbackTopicEnrolmentId}`
  },
  searchUrl(query: string): string {
    return `/search/${query}`
  },
  dashboardManage(dashboardId: number, tab?: string): string {
    return `/dashboard/${dashboardId}/manage${tab ? `/${tab}` : ''}`
  },
  campaigns(): string {
    return `/campaigns`
  },
  campaign(campaignId: number): string {
    return `/campaign/${campaignId}`
  },
  campaignAudience(campaignId: number): string {
    return `/campaign/${campaignId}/audience`
  },
  campaignNewEmail(campaignId: number): string {
    return `/campaign/${campaignId}/new-email`
  },
  campaignNewManagerNotification(campaignId: number): string {
    return `/campaign/${campaignId}/new-manager-notification`
  },
  campaignNewBanner(campaignId: number): string {
    return `/campaign/${campaignId}/new-banner`
  },
  campaignNewTopicShareAction(
    campaignId: number,
    sharedIds?: number[]
  ): string {
    if (sharedIds) {
      return `/campaign/${campaignId}/new-topic-share?${queryString.stringify({
        selectedIds: sharedIds
      })}`
    }
    return `/campaign/${campaignId}/new-topic-share`
  },
  campaignNewPathwayShareAction(
    campaignId: number,
    sharedIds?: number[]
  ): string {
    if (sharedIds) {
      return `/campaign/${campaignId}/new-pathway-share?${queryString.stringify(
        {
          selectedIds: sharedIds
        }
      )}`
    }
    return `/campaign/${campaignId}/new-pathway-share`
  },
  campaignNewSurveyShareAction(
    campaignId: number,
    sharedIds?: number[]
  ): string {
    if (sharedIds) {
      return `/campaign/${campaignId}/new-survey-share?${queryString.stringify({
        selectedIds: sharedIds
      })}`
    }
    return `/campaign/${campaignId}/new-survey-share`
  },
  campaignNewCertificationShareAction(
    campaignId: number,
    sharedIds?: number[]
  ): string {
    if (sharedIds) {
      return `/campaign/${campaignId}/new-certification-share?${queryString.stringify(
        {
          selectedIds: sharedIds
        }
      )}`
    }
    return `/campaign/${campaignId}/new-certification-share`
  },
  campaignNewMessage(campaignId: number): string {
    return `/campaign/${campaignId}/new-message`
  },

  campaignEmail(campaignId: number, emailId: number): string {
    return `/campaign/${campaignId}/email/${emailId}`
  },
  campaignBanner(campaignId: number, bannerId: number): string {
    return `/campaign/${campaignId}/banner/${bannerId}`
  },
  campaignTopicShareAction(campaignId: number, topicId: number): string {
    return `/campaign/${campaignId}/topic-share/${topicId}`
  },
  campaignPathwayShareAction(campaignId: number, pathwayId: number): string {
    return `/campaign/${campaignId}/pathway-share/${pathwayId}`
  },
  campaignCertificationShareAction(
    campaignId: number,
    certificationId: number
  ): string {
    return `/campaign/${campaignId}/certification-share/${certificationId}`
  },
  campaignSurveyShareAction(campaignId: number, surveyId: number): string {
    return `/campaign/${campaignId}/survey-share/${surveyId}`
  },
  campaignMessage(campaignId: number, messageId: number): string {
    return `/campaign/${campaignId}/message/${messageId}`
  },
  eventDetail(eventId: number): string {
    return `/event/${eventId}`
  },
  eventDetailSeries(eventId: number): string {
    return `/event/series/${eventId}/`
  },
  eventTag(tagId: number): string {
    return `/events/tag/${tagId}`
  },
  secretSelfSignup(token: string): string {
    return `/self-signup/${token}`
  },
  manageEvent(
    eventId: number,
    isRecurring: boolean = false,
    tab: '' | 'occurrences' | 'attendance' = ''
  ): string {
    return `${staticLinks.manageEvents}/${eventId}${
      isRecurring ? '/series' : ''
    }${tab ? `/${tab}` : ''}`
  },
  groupDetail(groupId: number): string {
    return `/group/id/${groupId}`
  },
  groups(): string {
    return `/groups`
  },
  pathwayDetail(pathwayId: number): string {
    return `/pathway/${pathwayId}`
  },
  pathwayDetailWithVersion(
    pathwayId: number,
    pathwayVersion: string,
    showVersionsDrawer = false
  ): string {
    return `/pathway/${pathwayId}/${pathwayVersion}${
      showVersionsDrawer ? '?showVersionsDrawer' : ''
    }`
  },
  userDetail(userId: string | number, tab?: string): string {
    return `/user/${userId}${tab ? `/${tab}` : ''}`
  },
  resetPasswordSent(email: string): string {
    return `/resetPassword/sent/${email}`
  },
  users(): string {
    return `/users/`
  },
  analyticsCertificationDetail(certificationId: number): string {
    return `${staticLinks.analyticsCertifications}/${certificationId}`
  },
  analyticsCertificationEnrolmentDetail(
    certificationId: number,
    enrolmentId: number
  ): string {
    return `${staticLinks.analyticsCertifications}/${certificationId}/enrolment/${enrolmentId}`
  },
  analyticsPathwayDetail(enrolmentId: number): string {
    return `/analytics/pathways/enrolment/${enrolmentId}`
  },
  analyticsResourceDetail(resourceId: number): string {
    return `/analytics/resources/${resourceId}`
  },
  analyticsWorkbooksDetail(workbookId: number): string {
    return `/analytics/workbooks/${workbookId}/views`
  },
  analyticsPeopleDetail(userId: number): string {
    return `/analytics/people/${userId}`
  },
  analyticsMediaDetail(topicElementId: number): string {
    return `/analytics/media/${topicElementId}`
  },
  awaitingVerification(email: string): string {
    return `/awaitingVerification/${email}`
  },
  resetPassword(userIdentity?: string): string {
    return `/resetPassword/${userIdentity ? userIdentity : ''}`
  },
  resetPasswordForm(userId: number, token: string): string {
    return `/resetPassword/${userId}/${token}`
  },
  activateUser(userId: number, token: string): string {
    return `/activate/${userId}/${token}`
  },
  verifyUser(userId: number, token: string): string {
    return `/verifyAccount/${userId}/${token}`
  },
  userImport(id: number): string {
    return `/users/import/${id}`
  },
  trackedLink(urlHash: string): string {
    return `/redirect/${urlHash}`
  },
  userCertifications(id: number): string {
    return `/user/certifications/${id}`
  },
  certificationsTemplates(id: number): string {
    return `/certificationsTemplates/${id}`
  },
  certificationsTemplatePreview(id: number): string {
    return `/certificationsTemplates/${id}/preview`
  },
  certificationsManage(id: number): string {
    return `/certifications/${id}/manage`
  },
  certificationsManageAccess(id: number): string {
    return `/certifications/${id}/manage/access`
  },
  certificationsDetail(id: number): string {
    return `/certifications/${id}`
  },
  videoAssessmentGradeBookUrl(topicId: number): string {
    return `/topic/${topicId}/videoAssessmentGradeBook`
  },
  verifyCustomSMTPDefaultEmailSender(token: string): string {
    return `/verifyCustomSMTPDefaultEmailSender/${token}`
  }
}

export const staticLinks = {
  login: '/login',
  selectSubdomain: '/selectSubdomain',
  home: '/',
  dashboards: '/dashboards',
  events: '/events',
  userImports: '/users/import',
  groups: '/groups',
  sharedResources: `/shared-resources`,
  users: '/users',
  managePathways: '/managePathways',
  pathways: '/pathways',
  campaigns: '/campaigns',
  certifications: '/certifications',
  certificationsManage: '/certificationsManage',
  certificationsTemplates: '/certificationsTemplates',
  surveys: '/surveys',
  analyticsPeople: '/analytics/people',
  analyticsResources: '/analytics/resources',
  analyticsWorkbooks: '/analytics/workbooks',
  analyticsPathways: '/analytics/pathways',
  analyticsMedia: '/analytics/media',
  analyticsSearch: '/analytics/search-stats',
  analyticsCertifications: '/analytics/certifications',
  manageEvents: '/manageEvents',
  accountSettings: '/account/settings',
  accountAuthenticationOptions: '/account/authenticationOptions',
  accountNotifications: '/account/notifications',
  accountCampaigns: '/account/campaigns',
  accountProvisioning: '/account/provisioning',
  accountCustomTranslations: '/account/customTranslations',
  noOrganisation: '/noOrganisation',
  trackedLinkRedirect: '/redirect',
  emailBlocklist: '/emailBlocklist',
  userBasicTab: '/user/me/basic',
  activityHistory: '/activity-history'
}

// TODO: add all routes ignored by back button
export const ignoredBackButtonRoutes = [
  '/(.*)?/topic/:topicId/(edit|preview|videoAssessmentGradeBook)?',
  '/surveys/:id/manage/(create|send|results|preview)?',
  '/user/me/(basic|additional|password|cpd-portfolio|accessibility|authentication)?',
  '/user/:id/(basic|additional|password|cpd-portfolio|accessibility|authentication)?',
  '/manageEvents/:id/(attendance|comments)?',
  '/account/(settings|authenticationOptions|notifications|campaigns|provisioning|certificateTemplates|customTranslations)?',
  '/dashboard/:id/manage/(audience|setup|collaborators)?',
  '/campaign/:id/(.*)?',
  '/certifications/:id/manage/(access|template)?',
  '/managePathway/:id/(access)?'
]
